import { computed, inject, Injectable } from '@angular/core';
import {
  VERTICAL_SCROLL_OFFSET_DESKTOP,
  VERTICAL_SCROLL_OFFSET_MOBILE,
} from '@core/config-angular';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { PlatformService } from '@core/platform';
import { ViewportScroller } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ScrollService {
  readonly #mediaObserverService = inject(MediaObserverService);
  readonly #platformService = inject(PlatformService);
  readonly #viewportScroller = inject(ViewportScroller);
  readonly #offsetDesktop = inject(VERTICAL_SCROLL_OFFSET_DESKTOP, {
    optional: true,
  });
  readonly #offsetMobile = inject(VERTICAL_SCROLL_OFFSET_MOBILE, {
    optional: true,
  });

  public readonly verticalScrollOffset = computed(() => {
    const { isDesktop } = this.#mediaObserverService.deviceInfo();
    return (isDesktop ? this.#offsetDesktop : this.#offsetMobile) ?? 0;
  });

  /**
   * Gets the vertical offset of the specified element relative to the viewport.
   *
   * NOTE: The vertical offset is taking the header height into account.
   *
   * @param {HTMLElement | null | undefined} element - The element for which to get the vertical offset.
   * @returns {number | undefined} The vertical offset of the element, or undefined if the element is null (or when executed on the SSR)
   */
  getElementVerticalOffset(
    element: HTMLElement | null | undefined
  ): number | undefined {
    if (this.#platformService.isClient()) {
      const offset = element?.getBoundingClientRect?.()?.top;
      if (offset) {
        const [_, scrollY] = this.#viewportScroller.getScrollPosition();
        return offset + scrollY - this.verticalScrollOffset();
      }
    }
    return undefined;
  }
}
